import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '/api';

const STAT_CARDS = [
  { label: 'Total Revenue', icon: '💰', key: 'revenue', prefix: '₹', color: '#FFB800' },
  { label: 'Total Orders', icon: '📦', key: 'orders', color: '#FF6B00' },
  { label: 'Products', icon: '🛍', key: 'products', color: '#4CAF50' },
  { label: 'Customers', icon: '👥', key: 'customers', color: '#9C27B0' },
];

export default function Dashboard() {
  const [stats, setStats] = useState({ revenue: 0, orders: 0, products: 0, customers: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    Promise.all([
      fetch(`${API}/get-dashboard-stats.php`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
      fetch(`${API}/get-orders.php?limit=5`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({ orders: [] })),
    ]).then(([s, o]) => {
      setStats({ revenue: s.revenue || 0, orders: s.orders || 0, products: s.products || 0, customers: s.customers || 0 });
      setRecentOrders(o.orders || []);
    }).finally(() => setLoading(false));
  }, []);

  const statusColor = (s) => ({ pending: '#FFB800', processing: '#FF6B00', shipped: '#2196F3', delivered: '#4CAF50', cancelled: '#C0392B' }[s] || '#888');

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '1.6rem', background: 'linear-gradient(135deg,#FFE082,#FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '.3rem' }}>Sacred Dashboard</h1>
        <p style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', color: 'rgba(255,224,130,.45)', fontStyle: 'italic' }}>ॐ — Your spiritual business at a glance</p>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '1.2rem', marginBottom: '2.5rem' }}>
        {STAT_CARDS.map(card => (
          <div key={card.key} style={{ background: 'linear-gradient(135deg,rgba(255,107,0,.08),rgba(36,0,70,.3))', border: '1px solid rgba(255,184,0,.18)', borderRadius: 10, padding: '1.5rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '.8rem', right: '1rem', fontSize: '2rem', opacity: .4 }}>{card.icon}</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(255,224,130,.5)', marginBottom: '.5rem' }}>{card.label}</div>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '1.8rem', color: card.color }}>
              {loading ? '…' : `${card.prefix || ''}${Number(stats[card.key]).toLocaleString('en-IN')}`}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
        {[
          { to: '/admin/orders', label: 'Manage Orders', icon: '📦' },
          { to: '/admin/products', label: 'Add Product', icon: '➕' },
          { to: '/admin/discounts', label: 'New Coupon', icon: '🏷️' },
          { to: '/admin/customers', label: 'Customers', icon: '👥' },
        ].map(q => (
          <Link key={q.to} to={q.to} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.5rem', padding: '1.2rem', background: 'rgba(255,184,0,.05)', border: '1px solid rgba(255,184,0,.15)', borderRadius: 8, textDecoration: 'none', fontFamily: "'Cinzel',serif", fontSize: '.68rem', letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(255,224,130,.7)', transition: 'all .2s' }}>
            <span style={{ fontSize: '1.5rem' }}>{q.icon}</span>
            {q.label}
          </Link>
        ))}
      </div>

      {/* Recent Orders */}
      <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.95),rgba(26,10,46,.95))', border: '1px solid rgba(255,184,0,.18)', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '1.2rem 1.5rem', borderBottom: '1px solid rgba(255,184,0,.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontFamily: "'Cinzel',serif", fontSize: '.75rem', letterSpacing: '.2em', textTransform: 'uppercase', color: '#FFB800' }}>📦 Recent Orders</h3>
          <Link to="/admin/orders" style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.1em', color: '#FF6B00', textDecoration: 'none' }}>View All →</Link>
        </div>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,224,130,.4)', fontFamily: "'Cinzel',serif", fontSize: '.75rem' }}>🌀 Loading orders…</div>
        ) : recentOrders.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,224,130,.4)', fontFamily: "'Cinzel',serif", fontSize: '.75rem' }}>No orders yet. Sacred products await seekers 🙏</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,184,0,.1)' }}>
                {['Order #', 'Customer', 'Amount', 'Status', 'Date'].map(h => (
                  <th key={h} style={{ padding: '.7rem 1rem', textAlign: 'left', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(255,184,0,.5)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentOrders.map(order => (
                <tr key={order.id} style={{ borderBottom: '1px solid rgba(255,184,0,.06)' }}>
                  <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel',serif", fontSize: '.72rem', color: '#FFE082' }}>#{order.id}</td>
                  <td style={{ padding: '.75rem 1rem', fontFamily: "'Crimson Pro',serif", fontSize: '.85rem', color: 'rgba(255,224,130,.7)' }}>{order.customer_name || 'Guest'}</td>
                  <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel',serif", fontSize: '.78rem', color: '#FFB800' }}>₹{Number(order.total_amount || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '.75rem 1rem' }}>
                    <span style={{ background: `${statusColor(order.status)}22`, color: statusColor(order.status), border: `1px solid ${statusColor(order.status)}44`, borderRadius: 20, padding: '.2rem .7rem', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>{order.status || 'pending'}</span>
                  </td>
                  <td style={{ padding: '.75rem 1rem', fontFamily: "'Crimson Pro',serif", fontSize: '.8rem', color: 'rgba(255,224,130,.4)' }}>{order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
